import { createContext, useState } from "react";

import { ProjectedChartProviderProps, ProjectedChartState } from "./types";
import {
  buildBarChart,
  calculateInvestedProgression,
  calculateProjectedProgression,
} from "../../utils";
import dayjs from "dayjs";
import { SipFrequency } from "../../types";
import { constants, spacing, theme } from "../../layout";
import {
  DEFAULT_CONTRIBUTE_AFTER_18,
  DEFAULT_MULTIPLIER,
  DEFAULT_PROJECTED_AGE,
  DEFAULT_RECURRING_AMOUNT,
} from "../../constants";
import { useWindowDimensions } from "../../hooks";

export const ProjectedChartContext = createContext<ProjectedChartState>(
  {} as ProjectedChartState
);

export const ProjectedChartProvider = ({
  currentAge: currentAgeProp = 0,
  children,
  recurringAmount: customRecurringAmount = DEFAULT_RECURRING_AMOUNT,
  frequency: customFrequency = SipFrequency.Weekly,
  initialBalance: initialBalanceProp = 0,
  investedColor: investedColorProp,
  returnedColor: returnedColorProp,
  projectedAge: projectedAgeProp,
  multiplier: multiplierProp,
  contributeAfter18: contributeProp,
}: ProjectedChartProviderProps) => {
  const investedColor = investedColorProp ?? theme.chart.invested;
  const returnedColor = returnedColorProp ?? theme.chart.returned;

  const defaultMultiplier = multiplierProp ?? DEFAULT_MULTIPLIER;
  const defaultContributeAfter18 =
    contributeProp ?? DEFAULT_CONTRIBUTE_AFTER_18;
  const defaultProjectedAge = projectedAgeProp ?? DEFAULT_PROJECTED_AGE;

  const [currentAge, setCurrentAge] = useState(currentAgeProp);
  const [projectedAge, setProjectedAge] = useState(defaultProjectedAge);
  const [multiplier, setMultiplier] = useState(defaultMultiplier);
  const [contributeAfter18, setContributeAfter18] = useState(
    defaultContributeAfter18
  );
  const [initialBalance, setInitialBalance] = useState(initialBalanceProp);

  const [recurringAmount, setRecurringAmount] = useState(customRecurringAmount);
  const [recurringDepositType, setRecurringDepositType] =
    useState<SipFrequency>(customFrequency);
  const [isInfoVisible, setIsInfoVisible] = useState(false);

  const { width: dimensionWidth } = useWindowDimensions();

  const interest = Math.round((multiplier - 1) * 100);

  const invested = calculateInvestedProgression({
    amount: recurringAmount,
    recurringDepositType,
    currentAge,
    projectedAge,
    contributeAfter18,
    initialBalance,
  });

  const projected = calculateProjectedProgression({
    amount: recurringAmount,
    recurringDepositType,
    currentAge,
    projectedAge,
    multiplier,
    contributeAfter18,
    initialBalance,
  });

  const targetYear = projectedAge - currentAge + dayjs().year();

  const investedAmount = invested[invested.length - 1].amount;
  const projectedAmount = projected[projected.length - 1].amount;

  const returnedAmount = projectedAmount - investedAmount;

  const minWidth = dimensionWidth - constants.divPadding * 2 - spacing.s * 2;
  const width = Math.min(minWidth, constants.chart.width);

  const [x, y] = buildBarChart({ invested, projected, width });

  const props = {
    value: {
      recurringAmount,
      setRecurringAmount,
      invested,
      projected,
      currentAge,
      setCurrentAge,
      projectedAge,
      setProjectedAge,
      x,
      y,
      recurringDepositType,
      setRecurringDepositType,
      multiplier,
      setMultiplier,
      isInfoVisible,
      setIsInfoVisible,
      contributeAfter18,
      setContributeAfter18,
      initialBalance,
      setInitialBalance,
      investedColor,
      returnedColor,
      targetYear,
      investedAmount,
      projectedAmount,
      returnedAmount,
      interest,
      width,
    },
  };
  return (
    <ProjectedChartContext.Provider {...props}>
      {children}
    </ProjectedChartContext.Provider>
  );
};
